import { z } from 'zod/v4';
import {
  BUILT_FORM_VALUES,
  CONSTRUCTION_AGE_BAND_VALUES,
  HEATING_SYSTEM_VALUES,
  OCCUPANCY_ARCHETYPE_VALUES,
  PROPERTY_TYPE_VALUES,
  WALL_CONSTRUCTION_VALUES,
  WALL_INSULATION_VALUES,
  WINDOW_GLAZING_VALUES,
} from './propertyInfoOptions';

/**
 * The two contexts this form is used in:
 * - `spruce`: the Push to Spruce flow, which requires the fabric fields.
 * - `heatLoss`: manual entry on the Property tab to drive a heat loss estimate,
 *   which ignores the fabric fields but needs the construction age band.
 */
export type PropertyInfoVariant = 'spruce' | 'heatLoss';

/**
 * Numeric fields are kept as strings because they back `type="number"` inputs.
 * These constraints describe the number the string must represent.
 */
type NumberStringConstraints = {
  /** Lower bound the parsed number must satisfy. */
  min: number;
  /** When true the value must be strictly greater than `min`. */
  exclusiveMin?: boolean;
  /** When true the value must be a whole number. */
  integer?: boolean;
};

const isValidNumberString = (
  value: string,
  { min, exclusiveMin, integer }: NumberStringConstraints
): boolean => {
  // Only plain decimal digits, matching how the values are later serialised
  // with parseInt/parseFloat. A bare Number() would accept exponent notation
  // ("1e3" → 1000) that parseInt then mangles to 1, silently corrupting saves.
  const pattern = integer ? /^\d+$/ : /^\d+(\.\d+)?$/;
  if (!pattern.test(value)) return false;
  const parsed = Number(value);
  return exclusiveMin ? parsed > min : parsed >= min;
};

/** A required numeric string: non-empty and representing a valid number. */
const requiredNumberString = (
  constraints: NumberStringConstraints,
  messages: { required: string; invalid: string }
) =>
  z
    .string()
    .min(1, messages.required)
    .refine(
      (value) => isValidNumberString(value, constraints),
      messages.invalid
    );

/** An optional numeric string: empty/absent, or representing a valid number. */
const optionalNumberString = (
  constraints: NumberStringConstraints,
  invalidMessage: string
) =>
  z
    .string()
    .optional()
    .refine(
      (value) =>
        value == null ||
        value === '' ||
        isValidNumberString(value, constraints),
      invalidMessage
    );

/**
 * Fabric fields describe the building envelope. They are only consumed by the
 * Spruce integration; the heat loss estimate ignores them. They are therefore
 * optional at the type level and only enforced as required per variant.
 */
const propertyInfoBaseSchema = z.object({
  propertyType: z.enum(PROPERTY_TYPE_VALUES, {
    message: 'Property type is required',
  }),
  builtForm: z.enum(BUILT_FORM_VALUES, {
    message: 'Built form is required',
  }),
  constructionAgeBand: z.enum(CONSTRUCTION_AGE_BAND_VALUES).optional(),
  floorAreaM2: requiredNumberString(
    { min: 0, exclusiveMin: true },
    { required: 'Floor area is required', invalid: 'Enter a valid floor area' }
  ),
  numBedrooms: requiredNumberString(
    { min: 0, integer: true },
    {
      required: 'Number of bedrooms is required',
      invalid: 'Enter a valid number of bedrooms',
    }
  ),
  heatingSystem: z.enum(HEATING_SYSTEM_VALUES, {
    message: 'Heating system is required',
  }),
  heatingEnergyUseKwh: optionalNumberString(
    { min: 0 },
    'Enter a valid annual heating energy use'
  ),
  electricityOccupancyArchetype: z.enum(OCCUPANCY_ARCHETYPE_VALUES).optional(),
  annualElectricityConsumptionKwh: z
    .number()
    .int()
    .min(0, 'Enter a valid annual electricity consumption')
    .nullable()
    .optional(),
  wallConstruction: z.enum(WALL_CONSTRUCTION_VALUES).optional(),
  wallInsulation: z.enum(WALL_INSULATION_VALUES).optional(),
  windowGlazingType: z.enum(WINDOW_GLAZING_VALUES).optional(),
  loftInsulationMm: optionalNumberString(
    { min: 0, integer: true },
    'Enter a valid loft insulation depth'
  ),
  numStoreys: optionalNumberString(
    { min: 0, integer: true },
    'Enter a valid number of storeys'
  ),
});

/** Messages for fields whose requiredness depends on the variant. */
const CONDITIONAL_REQUIRED_MESSAGES = {
  wallConstruction: 'Wall construction is required',
  wallInsulation: 'Wall insulation is required',
  windowGlazingType: 'Window glazing type is required',
  loftInsulationMm: 'Loft insulation is required',
  constructionAgeBand: 'Construction age band is required',
} as const;

type ConditionalField = keyof typeof CONDITIONAL_REQUIRED_MESSAGES;

/**
 * Single source of truth for which variant-dependent fields each variant
 * requires. Consumed by both {@link buildPropertyInfoSchema} (validation) and
 * {@link isFieldRequired} (the `*` on field labels), so the two cannot drift.
 * The always-required core fields are enforced by the base schema instead.
 */
const VARIANT_REQUIRED_FIELDS = {
  spruce: [
    'wallConstruction',
    'wallInsulation',
    'windowGlazingType',
    'loftInsulationMm',
  ],
  heatLoss: ['constructionAgeBand'],
} as const satisfies Record<PropertyInfoVariant, readonly ConditionalField[]>;

/** Whether a variant-dependent field is required in the given variant. */
export const isFieldRequired = (
  variant: PropertyInfoVariant,
  field: ConditionalField
): boolean => {
  const requiredFieldsForVariant: readonly ConditionalField[] =
    VARIANT_REQUIRED_FIELDS[variant];
  return requiredFieldsForVariant.includes(field);
};

/** Build the property-info form schema for the given variant. */
export const buildPropertyInfoSchema = (variant: PropertyInfoVariant) =>
  propertyInfoBaseSchema.check((ctx) => {
    for (const field of VARIANT_REQUIRED_FIELDS[variant]) {
      if (!ctx.value[field]) {
        ctx.issues.push({
          input: ctx.value[field],
          code: 'custom',
          path: [field],
          message: CONDITIONAL_REQUIRED_MESSAGES[field],
          continue: true,
        });
      }
    }
  });

export type PropertyInfoFormValues = z.infer<typeof propertyInfoBaseSchema>;
